import type { useTranslations } from 'next-intl';

/**
 * The relative-time formatter, kept in its own module with no runtime imports.
 *
 * It lives apart from time-helper.ts because that module calls `useTranslations` at runtime, which
 * makes it unloadable in the `node --test` harness (which runs with no node_modules). Extracting
 * the pure part is the fix tests-run-without-install.test.mjs recommends by name, and the same
 * shape as packages/ui/src/table/row-reorder-position.ts.
 *
 * Note the key contract: the keys below are FULLY QUALIFIED, so callers must pass a root-scoped
 * `t`. A `useTranslations('common')` here asks for 'common.common.time.before.*' and every
 * timestamp breaks — which is exactly what admin and distributor did (CIN-14663).
 */
export type TranslateFn = ReturnType<typeof useTranslations>;

/**
 * Pure relative-time formatter ("vor 3 Stunden" / "3 hours ago" / "Gerade eben").
 * Takes the next-intl translation function explicitly so it can be reused inside
 * other hooks without triggering a nested `useTranslations()` call (rules of hooks).
 */
export function getTimeAgo(fromDateTimestamp: Date | string, toDate: Date | null, t: TranslateFn): string {
    const _fromDate: Date = new Date(fromDateTimestamp);
    const _toDate: Date = toDate || new Date();
    const _diffMs = _toDate.getTime() - _fromDate.getTime();
    const seconds: number = _diffMs / 1000;
    const minutes: number = _diffMs / 1000 / 60;
    const hours: number = _diffMs / 1000 / 60 / 60;
    const days: number = _diffMs / 1000 / 60 / 60 / 24;

    // Seeded rather than left unassigned: the chain below has no final `else`, and a diff of 0
    // returns the "just now" string without ever reading `unit`.
    let unit = '';
    let diff = 0;

    if (seconds < 60) {
        if (Math.floor(seconds) == 1) {
            unit = t('common.time.before.second');
        } else {
            unit = t('common.time.before.seconds');
        }
        diff = seconds; // SECONDS
    } else if (minutes < 60) {
        if (Math.floor(minutes) == 1) {
            unit = t('common.time.before.minute');
        } else {
            unit = t('common.time.before.minutes');
        }
        diff = minutes; // MINUTES
    } else if (hours < 24) {
        if (Math.floor(hours) == 1) {
            unit = t('common.time.before.hour');
        } else {
            unit = t('common.time.before.hours');
        }
        diff = hours; // HOURS
    } else if (days >= 7) {
        if (days > 365) {
            if (Math.floor(days / 365) == 1) {
                unit = t('common.time.before.year');
            } else {
                unit = t('common.time.before.years');
            }
            diff = days / 365; // YEARS
        } else if (days > 30) {
            if (Math.floor(days / 30) == 1) {
                unit = t('common.time.before.month');
            } else {
                unit = t('common.time.before.months');
            }
            diff = days / 30; // MONTHS
        } else {
            if (Math.floor(days / 7) == 1) {
                unit = t('common.time.before.week');
            } else {
                unit = t('common.time.before.weeks');
            }
            diff = days / 7; // WEEKS
        }
    } else if (days < 7) {
        if (Math.floor(days) == 1) {
            unit = t('common.time.before.day');
        } else {
            unit = t('common.time.before.days');
        }
        diff = days; // DAYS
    }

    if (Math.floor(diff) > 0) {
        return t('common.time.before.base', {
            0: Math.floor(diff).toString(),
            1: unit
        });
    }

    return t('common.time.before.just-now');
}
